import { certificateName } from "./certificateNaming.js";

/**
 * Sanitize a filename to prevent path traversal and invalid characters
 * @param {string} filename - The filename to sanitize
 * @returns {string} - Sanitized filename
 * @throws {Error} If filename becomes empty after sanitization (security)
 */
export function sanitizeFilename(filename) {
  // Convert to string (handles null/undefined)
  let sanitized = String(filename);

  // Handle empty string
  if (sanitized === "") {
    return "";
  }

  // Security: Remove potentially dangerous characters
  // Remove null bytes (prevent null byte injection)
  sanitized = sanitized.replace(/\0/g, "");
  // Remove newlines and carriage returns (prevent multi-line injection)
  sanitized = sanitized.replace(/[\n\r]/g, "");
  // Remove tabs
  sanitized = sanitized.replace(/\t/g, "");

  // Replace wildcard with 'wildcard' for certificate common names
  if (sanitized.startsWith("*.")) {
    sanitized = sanitized.replace("*.", "wildcard-");
  }

  // Replace @ with _at_ for email addresses
  sanitized = sanitized.replace(/@/g, "_at_");

  // Replace dots with hyphens, keep hyphens and underscores
  sanitized = sanitized.replace(/\./g, "-");

  // Security: Remove path traversal sequences AFTER other replacements
  // This prevents bypass scenarios where replacements might recreate .. sequences
  sanitized = sanitized.replace(/\.\./g, "");
  sanitized = sanitized.replace(/\.\\/g, "");
  sanitized = sanitized.replace(/\.\//g, "");

  // Remove or replace invalid characters (allow only a-zA-Z0-9, -, _)
  sanitized = sanitized.replace(/[^a-zA-Z0-9._-]/g, "_");

  // Security: Remove leading/trailing dashes and underscores
  sanitized = sanitized.replace(/^[-_]+|[-_]+$/g, "");

  // Security: Collapse multiple consecutive underscores/hyphens
  sanitized = sanitized.replace(/_{2,}/g, "_");
  sanitized = sanitized.replace(/-{2,}/g, "-");

  // Truncate to 50 characters max
  if (sanitized.length > 50) {
    sanitized = sanitized.substring(0, 50);
    // Re-trim trailing separators after truncation
    sanitized = sanitized.replace(/[-_]+$/, "");
  }

  // Security: If sanitization results in empty string, use fallback
  if (sanitized === "" && filename !== "") {
    sanitized = "file";
  }

  return sanitized;
}

/**
 * Generate timestamp in standardized format (YYYYMMDD-HHMMSS)
 * @returns {string} - Formatted timestamp
 */
export function generateTimestamp() {
  return new Date()
    .toISOString()
    .slice(0, 19)
    .replace(/[-:]/g, "")
    .replace("T", "-");
}

/**
 * Generate standardized filename for SSL tools downloads
 * @param {string} identifier - Domain, email, or common name
 * @param {string} type - File type (csr, privatekey, certificate, etc.)
 * @param {string} extension - File extension (txt, pfx, json, etc.)
 * @returns {string} - Standardized filename: {identifier}-{type}-{timestamp}.{extension}
 */
export function generateStandardFilename(identifier, type, extension) {
  const sanitized = sanitizeFilename(identifier);
  const timestamp = generateTimestamp();

  return `${sanitized}-${type}-${timestamp}.${extension}`;
}

/**
 * Generate a filename for a certificate download (Legacy - use generateStandardFilename)
 * @deprecated Use generateStandardFilename() instead
 * @param {Object} cert - Certificate object with info
 * @param {number} index - Certificate index in chain (optional)
 * @returns {string} - Generated filename with .crt.txt extension
 */
export function generateCertificateFilename(cert, index = null) {
  // The BARE name, never the display form : a filename cannot carry a
  // "+ N More SANs" suffix. An S/MIME certificate names itself only in
  // its Subject Alternative Names, and used to download as
  // "certificate-..." with the customer's own identity discarded ;
  // sanitizeFilename already turns an e-mail address into a safe
  // filename by replacing "@" with "_at_".
  const baseName = certificateName(cert.info) || "certificate";

  const sanitizedName = sanitizeFilename(baseName);
  const typeWithIndex =
    index !== null
      ? index === 0
        ? "certificate"
        : `intermediate-${index}`
      : "certificate";

  return generateStandardFilename(sanitizedName, typeWithIndex, "txt");
}
